import { getCountryDataList, getEmojiFlag } from "countries-list";
import { useMemo, useState } from "react";

import { ComboBoxResponsive } from "./ComboBoxResponsive";
import { SignInFormVariant } from "../signin/SignInSignUpForm";
import { cn } from "@punchup/utils/cn";

interface Props {
  postalCode: string;
  postalCodeInputRef?: React.RefObject<HTMLInputElement>;
  onPostalCodeChange: (value: string) => void;
  defaultCountryCode: string | null;
  onCountryCodeChange: (value: string) => void;
  isUS: boolean;
  errors?: {
    postalCode?: string;
    country?: string;
  };
  variant?: SignInFormVariant;
}

// sort countries alphabetically
const countries = getCountryDataList().sort((a, b) =>
  a.name > b.name ? 1 : -1
);

export default function PostalCodeFields({
  postalCode,
  postalCodeInputRef,
  onPostalCodeChange,
  defaultCountryCode,
  onCountryCodeChange,
  isUS,
  errors,
  variant = "normal",
}: Props) {
  const [enteredCountryValue, setEnteredCountryValue] = useState("");

  const initialCountryValue = useMemo(() => {
    return (
      countries
        .find((country) => country.iso2 === defaultCountryCode)
        ?.name.toLowerCase() ?? ""
    );
  }, [defaultCountryCode]);

  const onCountryChange = (value: string) => {
    setEnteredCountryValue(value);

    const countryCode = countries.find((country) => {
      return country.name.toLowerCase() === value;
    })?.iso2;

    if (countryCode) {
      onCountryCodeChange(countryCode);
    }
  };

  const postalCodeLabel = useMemo(
    () => (isUS ? "ZIP Code" : "Postal Code"),
    [isUS]
  );

  const postalCodePlaceholder = useMemo(
    () =>
      isUS
        ? variant === "minimal"
          ? "Enter ZIP Code"
          : "e.g. 10012"
        : "Enter postal code",
    [isUS, variant]
  );

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "flex flex-row",
          variant === "minimal" ? "gap-x-3 md:gap-x-4" : "gap-x-4 md:gap-x-5"
        )}
      >
        <div className="flex-1">
          <label
            htmlFor="zip-code"
            className={cn(
              variant === "minimal"
                ? "sr-only"
                : "font-sans font-semibold text-[16px] leading-7 mb-1"
            )}
          >
            {postalCodeLabel}
          </label>
          <input
            ref={postalCodeInputRef}
            title="zip-code"
            id="zip-code"
            name="zipCode"
            type="text"
            // required
            // aria-required="true"
            placeholder={postalCodePlaceholder}
            autoComplete="postal-code"
            onChange={(e) => onPostalCodeChange(e.target.value)}
            value={postalCode}
            className={cn(
              "w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px]",
              variant === "minimal" ? "px-3 py-2.5" : "px-4 py-3"
            )}
          />
        </div>
        <div className="w-[120px]">
          <label
            htmlFor="country"
            className={cn(
              variant === "minimal"
                ? "sr-only"
                : "font-sans font-semibold text-[16px] leading-7 mb-1"
            )}
          >
            Country
          </label>
          <ComboBoxResponsive
            value={enteredCountryValue || initialCountryValue}
            setValue={onCountryChange}
            variant={variant}
            label="country"
            options={countries.map((country) => ({
              label: `${getEmojiFlag(country.iso2)} ${country.name}`,
              shortLabel: `${getEmojiFlag(country.iso2)} ${country.iso3}`,
              value: country.name.toLowerCase(), // the country value for the input should be the country name so that it's easily searchable by the user
            }))}
          />
        </div>
      </div>
      {errors && (errors.country || errors.postalCode) && (
        <div className="text-sm text-red-500 mt-0.5">
          {errors.postalCode && (
            <p>
              {errors.postalCode}
              {errors.country && `, ${errors.country}`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
